/**
 * Alert - Inline status banner atom.
 *
 * Used for the "validation failed" / "saved" / "watch out" callouts
 * that appear inside form sections and at the top of pages. Until this
 * atom landed, every consumer was hand-rolling the same
 * bg-{color}-50 + border-{color}-200 + text-{color}-700 combo, so
 * variants drifted apart over time and dark-mode work would have
 * needed to touch every spot.
 *
 *   <Alert variant="error">Please fix the highlighted errors.</Alert>
 *   <Alert variant="success">Saved.</Alert>
 *
 * Pure presentation — no internal state. Consumers control whether the
 * alert is visible by rendering / not rendering it.
 */
import React from 'react';

export type AlertVariant = 'error' | 'success' | 'warning' | 'info';

/** Size preset — 'md' is the standard page/form banner, 'sm' is for compact
 *  field-level hints (e.g. a duplicate warning rendered directly under an input). */
export type AlertSize = 'sm' | 'md';

export interface AlertProps {
	/** Color treatment. Defaults to 'info'. */
	variant?: AlertVariant;
	/** Size preset controlling padding, gap, and font size — defaults to 'md'. */
	size?: AlertSize;
	/** Main message. Renders as the only child when no `children` is given. */
	children: React.ReactNode;
	/** Leading status icon. `true` uses the built-in glyph for the variant;
	 *  pass a node to supply your own. Defaults to none, so existing banners
	 *  are unchanged. */
	icon?: boolean | React.ReactNode;
	/** Optional small action slot rendered on the right (e.g. "Try again" link). */
	action?: React.ReactNode;
	/** When supplied, renders a dismiss (×) button on the right. Pages were
	 *  hand-rolling this same button into `action`, each with its own colors
	 *  and no touch target; keeping it on the atom means one 44px control that
	 *  already matches the variant. */
	onDismiss?: () => void;
	/** Accessible name for the dismiss button. Defaults to "Dismiss". */
	dismissLabel?: string;
	/** Optional extra className for the outer container. */
	className?: string;
}

/* Tailwind palette per variant. Centralizing the strings here is the
   whole point of the atom — touching one record retunes the whole UI. */
const VARIANT_CLASSES: Record<AlertVariant, string> = {
	error:   'bg-red-50    border-red-200    text-red-700',
	success: 'bg-green-50  border-green-200  text-green-700',
	warning: 'bg-amber-50  border-amber-200  text-amber-700',
	info:    'bg-blue-50   border-blue-200   text-blue-700',
};

/* Size presets. 'sm' keeps compact inline hints from ballooning into a
   full-width page banner while still flowing through the atom. */
const SIZE_CLASSES: Record<AlertSize, string> = {
	sm: 'gap-8px px-12px py-8px text-12px',
	md: 'gap-12px px-16px py-12px text-14px',
};

/* Built-in leading glyphs. The workflow steps were each inlining the same
   exclamation-circle SVG next to the same red chip; it belongs here. */
const VARIANT_ICON_PATHS: Record<AlertVariant, string> = {
	/* Exclamation in a circle — "something is wrong / pay attention" */
	error:   'M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z',
	warning: 'M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z',
	/* Check in a circle — "it worked" */
	success: 'M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
	/* Lowercase i in a circle — "here is something to know" */
	info:    'M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z',
};

/* Vertical pull-back for the 44px dismiss button, per size preset. Each value
   cancels that preset's own py-* so the control keeps a full touch target
   without making the banner taller than its size. */
const DISMISS_PULL_CLASSES: Record<AlertSize, string> = {
	sm: '-my-8px',
	md: '-my-12px',
};

/* Icon size per size preset — tracks the body text so the two stay balanced. */
const ICON_SIZE_CLASSES: Record<AlertSize, string> = {
	sm: 'h-14px w-14px',
	md: 'h-16px w-16px',
};

/* Dismiss-button tint per variant. The button inherits the banner's hue so it
   reads as part of the alert rather than a foreign control. */
const DISMISS_CLASSES: Record<AlertVariant, string> = {
	error:   'text-red-600   hover:text-red-800   hover:bg-red-100',
	success: 'text-green-600 hover:text-green-800 hover:bg-green-100',
	warning: 'text-amber-600 hover:text-amber-800 hover:bg-amber-100',
	info:    'text-blue-600  hover:text-blue-800  hover:bg-blue-100',
};

export const Alert: React.FC<AlertProps> = ({
	variant = 'info',
	size = 'md',
	children,
	icon = false,
	action,
	onDismiss,
	dismissLabel = 'Dismiss',
	className = '',
}) => {
	return (
		<div
			role={variant === 'error' ? 'alert' : 'status'}
			data-ref="atom-alert"
			className={[
				`alert alert--${variant} alert--${size}`,
				'flex items-center rounded-md border font-sans',
				SIZE_CLASSES[size],
				VARIANT_CLASSES[variant],
				className,
			].join(' ')}
		>
			{icon && (
				<span className="alert__icon flex-shrink-0">
					{icon === true ? (
						<svg className={ICON_SIZE_CLASSES[size]} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
							<path strokeLinecap="round" strokeLinejoin="round" d={VARIANT_ICON_PATHS[variant]} />
						</svg>
					) : (
						icon
					)}
				</span>
			)}
			<div className="alert__body flex-1 min-w-0">{children}</div>
			{action && <div className="alert__action flex-shrink-0">{action}</div>}
			{onDismiss && (
				/* Negative margins pull the 44px hit area back over the banner's
				   own padding, so the control is fully tappable without making
				   the alert taller than its size preset. */
				<button
					type="button"
					onClick={onDismiss}
					aria-label={dismissLabel}
					className={[
						'alert__dismiss inline-flex items-center justify-center flex-shrink-0',
						'w-44px h-44px -mr-8px rounded-full transition-colors',
						DISMISS_PULL_CLASSES[size],
						DISMISS_CLASSES[variant],
					].join(' ')}
				>
					<svg className="h-16px w-16px" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
						<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
					</svg>
				</button>
			)}
		</div>
	);
};

export default Alert;
